<template>
  <AutocompleteDropdownV2
    v-bind="$attrs"
    :items="autocompleteItems"
    :initial-query="value"
    text-field="name"
    value-field="name"
    v-on="$listeners"
    @push="handlePush($event)"
    @keydown="() => (isTouched = true)"
  >
    <template #item="{ item, withHighlight }">
      <div class="current-title-autocomplete__suggestion-title" v-html="withHighlight(item.name, value)" />
    </template>
    <template v-if="showAddNew" #last-item>
      <a class="current-title-autocomplete__new" @click.prevent="createSalesRole(value)">
        <div class="current-title-autocomplete__icon-container">
          <PlusIcon class="current-title-autocomplete__icon" />
        </div>

        Add new&nbsp;
        <span class="current-title-autocomplete__term">"{{ value }}"</span>
      </a>
    </template>
  </AutocompleteDropdownV2>
</template>

<script lang="ts">
import Vue from 'vue';
import AutocompleteDropdownV2 from '~/components/common/AutocompleteDropdownV2.vue';
import type { ISalesRole } from '~/types/community';
import type { CreateSalesRoleResponse } from '~/types/api';
import PlusIcon from '~/assets/svg/plus.vue.svg';

const MAX_ITEMS = 4;
const MIN_QUERY_LENGTH = 2;

type Data = {
  salesRoles: ISalesRole[];
  isTouched: boolean;
};

export default Vue.extend({
  name: 'CurrentTitleAutocomplete',
  components: {
    AutocompleteDropdownV2,
    PlusIcon,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    withAddNew: {
      type: Boolean,
      default: true,
    },
  },
  data(): Data {
    return {
      salesRoles: [],
      isTouched: false,
    };
  },
  computed: {
    autocompleteItems(): Array<ISalesRole> {
      // suggestions would not show if items empty
      return this.withSuggestion
        ? this.salesRoles
            .filter((salesRole: ISalesRole) => salesRole.name.toLowerCase().includes(this.value.toLowerCase()))
            .slice(0, MAX_ITEMS)
        : [];
    },
    withSuggestion(): boolean {
      return this.value.length >= MIN_QUERY_LENGTH && this.isTouched;
    },
    showAddNew(): boolean {
      return (
        Boolean(this.withAddNew) &&
        Boolean(this.value) &&
        !this.autocompleteItems.some((salesRole: ISalesRole) => this.value === salesRole.name) &&
        this.withSuggestion
      );
    },
  },
  mounted() {
    this.getSalesRoles();
  },
  methods: {
    async getSalesRoles(): Promise<void> {
      const { sales_roles: salesRoles } = await this.$api.$get('/api/war_room/sales_roles.json', {
        params: {
          custom: false,
        },
      });

      this.salesRoles = salesRoles;
    },
    async createSalesRole(name: string): Promise<ISalesRole | void> {
      try {
        const salesRole = await this.$api.$post<CreateSalesRoleResponse>('/api/war_room/sales_roles.json', { name });
        await this.getSalesRoles();

        return salesRole;
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      }
    },

    // Autocomplete returs own object
    handlePush(salesRole: ISalesRole & { value: string; text: string }): void {
      this.$emit('input', salesRole.name || salesRole.value);
    },
  },
});
</script>

<style scoped lang="scss">
.current-title-autocomplete {
  &__suggestion-title {
    padding: 12px 16px;
    margin-bottom: 1px; // create a space that simulates solid border between suggestion items
  }

  &__new {
    display: flex;
    flex-direction: row;
    padding: 12px 16px;
    align-items: center;
    color: var(--theme-color-text);
    background: var(--theme-color-card);
    white-space: nowrap;
    cursor: pointer;
  }

  &__icon-container {
    width: 32px;
    height: 32px;
    background: var(--theme-color-over-card);
    border-radius: 6px;

    @include flex-center;

    flex: 0 0 32px;
    margin-right: 8px;
  }

  &__icon {
    width: 24px;
    height: 24px;
  }

  &__term {
    @include inherit-font;
    @include multiline-overflow(1);

    color: var(--theme-color-sub-text);
  }
}

::v-deep .bravado-text-input-v2__field-input {
  &:focus {
    border-color: var(--blue-60) !important;
  }
}

::v-deep .autocomplete-dropdown-v2__results-list {
  border: none;
  box-shadow: var(--theme-elevation-05);
  max-height: unset;
  overflow: hidden;
  margin-top: 0;
  border-radius: 12px;
}

::v-deep .members-user-block-highlight {
  font-weight: 600;
}
</style>
